import { skipToken } from '@apollo/client/react'
import type { CircularProgressProps } from '@mui/material'
import {
  CircularProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
} from '@mui/material'
import { captureException } from '@sentry/browser'
import {
  type PropsWithChildren,
  type RefObject,
  Suspense,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { useAsyncFn } from 'react-use'
import { match } from 'ts-pattern'
import { withQuery } from 'ufo'
import FluentAdd24Filled from '~icons/fluent/add-24-filled'
import FluentDocumentEdit20Regular from '~icons/fluent/document-edit-20-regular'
import FluentDocumentProhibited20Regular from '~icons/fluent/document-prohibited-20-regular'
import FluentImageArrowCounterclockwise20Regular from '~icons/fluent/image-arrow-counterclockwise-20-regular'
import FluentMoreVertical20Filled from '~icons/fluent/more-vertical-20-filled'
import IcBaselineCheckCircle from '~icons/ic/baseline-check-circle'
import IcBaselineClose from '~icons/ic/baseline-close'
import IcBaselineError from '~icons/ic/baseline-error'
import IcOutlineAddPhotoAlternateIcon from '~icons/ic/outline-add-photo-alternate'
import IcRoundPlusIcon from '~icons/ic/round-plus'
import type { ArtworkPickerRef } from 'src/components/ArtworkList/ArtworkPicker'
import ArtworkPicker from 'src/components/ArtworkList/ArtworkPicker'
import { ImageSource } from 'src/components/Common/ImageSource'
import type { UseModelArtworkDataSourceOptions } from 'src/components/Common/MediaPicker'
import { ModelArtworkPicker } from 'src/components/Common/MediaPicker'
import MediaPreview from 'src/components/Common/MediaPreview'
import UploadImage from 'src/components/Common/UploadImage'
import {
  CropCurrentImageForUploadButton,
  dialogCropImage,
} from 'src/components/CropImage'
import type { BaseCursorPagingRef } from 'src/components/CursorPaging/BaseCursorPaging'
import { getBaseCursorPagingArgs } from 'src/components/CursorPaging/cursorPagingUtils'
import {
  type ModelTypeTagColors,
  useGenerationModelColorConfig,
} from 'src/components/GenerationModel/modelTypeDisplay'
import TablePaginationActionsWrapper from 'src/components/MuiWrapper/TablePaginationActionsWrapper'
import { Preloader } from 'src/components/Preloader/Preloader'
import { UIButton } from 'src/components/ui/Button'
import type { UIMenuItemProps } from 'src/components/ui/Menu'
import { UIMenu, UIMenuItem, UIMenuTrigger } from 'src/components/ui/Menu'
import {
  UIDialog,
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
} from 'src/components/ui/Modal'
import Text from 'src/components/ui/Text'
import { useGetArtwork } from 'src/services/apollo/artwork'
import { useMyInfo } from 'src/services/apollo/auth'
import type {
  GenerationModelType,
  GenerationModelVersion,
} from 'src/services/apollo/generated/graphql'
import {
  executeUpsertGenerationModelVersion,
  fetchGetGenerationModelVersion,
  useGetGenerationModelByVersionId,
  useListGenerationModelVersions,
} from 'src/services/apollo/generation-model'
import { useGetMedia } from 'src/services/apollo/media'
import { ExternalModelStatus } from 'src/services/helpers/model'
import {
  getCreateVersionInputs,
  getSubmitModelVersionTrackAttrs,
} from 'src/services/helpers/modelApiParamsPacker'
import type { GenerationModelVersionExtra } from 'src/services/helpers/schema/modelVersionExtra'
import {
  blobSha256Base64,
  uploadFileMultiPart,
} from 'src/services/helpers/uploadMultipartFile'
import { cx } from 'src/utils/cx'
import { formatBytes } from 'src/utils/formatNum'
import { getNodesFromConnection } from 'src/utils/graphql'
import {
  getModelTypeFromVersion,
  hasModelTypeDisabledAddVersion,
  isLora,
  isUserMultiLora,
} from 'src/utils/model'
import { track } from 'src/utils/tracker'
import type { HandleChange } from 'src/utils/useForm'
import { useForm } from 'src/utils/useForm'
import { mapOrpcMediaToMedia } from '~/services/orpc/utils/typeAdapters'
import {
  getModelLoraBaseModelChipConfig,
  ModelVersionStatusChip,
} from '../../ModelTypeChip'
import { dialogModelRequestEdit } from '../ModelEditRequest'
import { ExternalModelImporterContext } from './context'
import type {
  ModelSubmitFormValues,
  VersionValues,
} from './ModelSubmitFormProvider'
import ModelVersionForm from './ModelVersionForm'

function CircularProgressWithLabel(
  props: CircularProgressProps & {
    value: number | undefined
  },
) {
  return (
    <div className="relative inline-flex">
      <CircularProgress variant="determinate" {...props} />
      {props.value != null && (
        <div className="inset-0 absolute flex items-center justify-center">
          <span className="text-xs text-gray-600 dark:text-gray-300">{`${Math.round(props.value)}%`}</span>
        </div>
      )}
    </div>
  )
}

type UploadSteps = 'ready' | 'hashing' | 'uploading' | 'done'
const allowedFileExtensions = ['.ckpt', '.pt', '.safetensors']

function ModelFileUploadBtn(props: {
  fileId?: string
  setFileId: (fileId?: string) => void
  skipHash?: boolean
  setFileUploading?: (uploading: boolean) => void
  values?: ModelSubmitFormValues
}) {
  const [fileInfo, setFileInfo] = useState<{
    name: string
    size: number
  } | null>(null)
  const { t } = useTranslation(['market', 'common', 'action', 'artwork'])
  const [progress, setProgress] = useState<undefined | number>(undefined)
  const [uploadStep, setUploadStep] = useState<UploadSteps>('ready')
  const { data: userInfo } = useMyInfo()

  const abortControllerRef = useRef<AbortController>(null)

  const [{ loading: fileUploading }, uploadFile] = useAsyncFn(
    async (file: File) => {
      abortControllerRef.current = new AbortController()
      try {
        props.setFileUploading?.(true)
        setUploadStep('hashing')
        const hash = await blobSha256Base64(file, {
          abortSignal: abortControllerRef.current.signal,
        })

        // skip hash for admin
        if (!props.skipHash) {
          const { data } = await fetchGetGenerationModelVersion({
            fileHash: hash,
          })
          const existVersion = data?.generationModelVersion

          if (existVersion) {
            await dialogModelRequestEdit(existVersion.modelId, {
              title: props.values?.title,
              mediaId: props.values?.media?.id,
              extra: {
                description: props.values?.extra?.description,
                triggerWords: props.values?.firstVersionExtra?.triggerWords,
              },
            })

            if (!userInfo?.isAdmin) {
              props.setFileId(undefined)
              setUploadStep('ready')
              setProgress(undefined)
              setFileInfo(null)
              return
            }
            // allow admin to confirm upload
            if (userInfo?.isAdmin) {
              setUploadStep('done')
              setProgress(undefined)
              props.setFileId(existVersion.fileUploadId)
              toast.info(
                'Because you are an admin, duplicated model files are allowed',
              )
              return
            }
          }
        }

        setUploadStep('uploading')
        setProgress(0)
        const uploadRes = await uploadFileMultiPart(file, hash, {
          onProgress: percentage => {
            setProgress(percentage * 100)
          },
          abortSignal: abortControllerRef.current.signal,
        })

        if (!uploadRes?.uploadId) throw new Error('upload failed')
        track('submit_file_success')

        props.setFileId(uploadRes.uploadId)
        setUploadStep('done')
        toast.success(t('market:submit.file-uploaded'))
      } catch (e: any) {
        props.setFileId(undefined)
        setFileInfo(null)
        track('submit_file_failed', {
          error: e.message,
        })
        captureException(e)
        if (e.name === 'AbortError' || e.name === 'CanceledError') {
          toast.info('File upload cancelled')
        } else {
          toast.error('Failed to upload file: ' + e.message)
        }
        setUploadStep('ready')
      } finally {
        setProgress(undefined)
        props.setFileUploading?.(false)
      }
    },
    [userInfo?.isAdmin],
  )

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const [file] = acceptedFiles
      if (!file) return
      // disallow non-model files
      if (!allowedFileExtensions.some(ext => file.name.endsWith(ext))) {
        return toast.error(t('market:submit.notice.invalid-file-extension'))
      }
      if (
        isLora(props.values?.type) &&
        file.size > 2 ** 30 // 1GiB
      ) {
        return toast.error(t('market:submit.notice.lora-max-size-exceeded'))
      }
      setFileInfo({ name: file.name, size: file.size })
      uploadFile(file)
    },
    [uploadFile, props.values?.type],
  )

  const { getRootProps, getInputProps, isDragActive, acceptedFiles } =
    useDropzone({
      onDrop,
      maxFiles: 1,
    })

  const onClose = () => {
    // TODO: support cancel
    abortControllerRef.current?.abort()
    setUploadStep('ready')
    setFileInfo(null)
    props.setFileId(undefined)
  }

  return (
    <div className="w-60 relative mt-2">
      <button
        className={cx(
          'border-2 border-dashed border-active text-primary font-medium py-8 rounded-xl flex flex-col items-center gap-1 w-full',
          (fileUploading || uploadStep !== 'ready') &&
            'pointer-events-none cursor-not-allowed',
          uploadStep === 'done' && 'bg-sky-100',
        )}
        disabled={fileUploading || uploadStep !== 'ready'}
        title={fileInfo?.name}
        {...getRootProps({
          onClick: () => {
            track('submit_file_click')
          },
        })}
      >
        <input
          {...getInputProps({
            accept: allowedFileExtensions.join(', '),
          })}
        />
        {uploadStep !== 'ready' && uploadStep !== 'done' && (
          <CircularProgressWithLabel
            variant={uploadStep === 'uploading' ? 'determinate' : undefined}
            value={progress}
            size={32}
            className="text-blue-800"
          />
        )}
        <span className="font-semibold">
          {uploadStep === 'ready'
            ? t('market:submit.submit-file')
            : uploadStep === 'hashing'
              ? t('market:submit.asset-form.hashing')
              : uploadStep === 'uploading'
                ? t('market:submit.file-uploading')
                : t('market:submit.file-uploaded')}
        </span>
        {fileInfo && (
          <div className="w-full">
            <p className="truncate font-mono text-xs w-full text-center">
              {formatBytes(fileInfo.size)}
            </p>
            <p className="truncate font-mono text-xs w-full text-center">
              {fileInfo.name}
            </p>
          </div>
        )}
      </button>
      {uploadStep !== 'ready' && (
        <UIButton
          variant="filled"
          color="normal"
          size="small"
          className="rounded-full z-10 absolute right-0 top-0 translate-x-1/3 -translate-y-1/3"
          onPress={onClose}
        >
          <IcBaselineClose className="size-3" />
        </UIButton>
      )}
    </div>
  )
}

export default function ModelAssetForm(props: {
  isEditing?: boolean
  values: ModelSubmitFormValues
  handleChange: HandleChange<keyof ModelSubmitFormValues>
  onClose?: () => void
}) {
  const { t } = useTranslation(['market'])
  const { values, handleChange, isEditing } = props
  const { modelId, media } = values
  const pagingRef = useRef<BaseCursorPagingRef>(null)

  const canAddVersion = !hasModelTypeDisabledAddVersion(values.type)

  const {
    modelInfo,
    status: externalModelStatus,
    error,
  } = useContext(ExternalModelImporterContext)

  return (
    <div className="w-full flex flex-col gap-4">
      {!isEditing && (
        <section>
          <h2 className="text-primary font-semibold text-lg md:text-2xl flex items-center gap-1 mb-1">
            {t('market:submit.model-cover.title')}
          </h2>
          <UploadImage
            value={mapOrpcMediaToMedia(modelInfo?.mediaList?.[0]) || media}
            onChange={m => {
              if (!m) return
              handleChange('media')(m)
            }}
            dropzoneOptions={{
              accept: {
                'image/jpeg': [],
                'image/png': [],
                'image/webp': [],
                // disallow gif images
              },
              // Prevent conflicting with RAC's focus hook
              noKeyboard: true,
            }}
            preHandleImage={async file => {
              const parsed = await dialogCropImage({ file })
              return (parsed as File) ?? file
            }}
            placeholderElement={<UploadImagePlacerholder />}
            className="relative"
            fileHandleElement={({ action }) => (
              <div className="absolute bottom-4 font-sans">
                <CropCurrentImageForUploadButton
                  media={values.media}
                  action={action}
                />
              </div>
            )}
          />
        </section>
      )}
      <section>
        <div className="flex items-center">
          <h2 className="text-primary font-semibold text-lg md:text-2xl flex items-center gap-1 mb-1">
            {t('market:submit.model-version.title')}
          </h2>
          {isEditing && (
            <AddModelVersionBtn
              disabled={!canAddVersion}
              className="ml-auto"
              modelProps={{
                id: values.modelId,
                type: values.type,
                isPrivate: values.isPrivate,
              }}
              onSubmit={() => {
                pagingRef.current?.mutate()
              }}
              onClose={props.onClose}
              modelId={values.modelId}
            />
          )}
        </div>
        {!isEditing &&
          match(externalModelStatus)
            .with(
              ExternalModelStatus.Validating,
              ExternalModelStatus.Uploading,
              () => (
                <div className="mt-2 inline-flex items-center gap-2 px-6 py-8 border-2 border-dashed border-active rounded-xl text-primary-light font-medium">
                  <CircularProgress color="inherit" size={16} />
                  {t('market:importer.messages.fetching-in-progress')}
                </div>
              ),
            )
            .with(ExternalModelStatus.Error, () => (
              <div className="mt-2 flex items-center gap-2 px-6 py-8 border-2 border-dashed border-red-600 rounded-xl">
                <IcBaselineError className="size-4 text-red-600" />
                {t('market:importer.messages.fetching-failed', {
                  error: error.toString(),
                })}
              </div>
            ))
            .with(ExternalModelStatus.Success, () => (
              <div className="mt-2 inline-flex items-center gap-2 px-6 py-8 border-2 border-dashed border-active rounded-xl text-primary-light font-medium">
                <IcBaselineCheckCircle className="size-4 text-green-600" />
                {t('market:importer.messages.fetching-succeeded')}
              </div>
            ))
            .otherwise(() => (
              <ModelFileUploadBtn
                fileId={values.uploadId}
                skipHash={values.isPrivate}
                setFileId={id => handleChange('uploadId')(id)}
                values={values}
              />
            ))}
        {isEditing && modelId && (
          <ModelVersions
            pagingRef={pagingRef}
            modelId={modelId}
            modelType={values.type}
            onClose={props.onClose}
          />
        )}
      </section>
    </div>
  )
}

type ModelPropsForVersionEdit = {
  id?: string
  type?: GenerationModelType
  isPrivate?: boolean
}

function AddModelVersionBtn(props: {
  className?: string
  modelId?: string
  modelProps: ModelPropsForVersionEdit
  onSubmit?: () => void
  disabled?: boolean
  onClose?: () => void
}) {
  const navigate = useNavigate()
  const { t } = useTranslation(['market'])
  const [openDialog, setOpenDialog] = useState(false)

  const onClose = () => {
    setOpenDialog(false)
  }

  const onAddVersion = useCallback(() => {
    track('add_version_click', {
      location: 'edit-model',
      modelType: props.modelProps.type,
    })
    if (isUserMultiLora(props.modelProps.type)) {
      navigate(withQuery('/train-lora', { addToModelId: props.modelId }))
      props.onClose?.()
    } else {
      setOpenDialog(true)
    }
  }, [props.modelId, props.modelProps.type])

  return (
    <>
      <UIButton
        disabled={props.disabled}
        className={cx(props.className)}
        variant="filled"
        onPress={onAddVersion}
        icon={<IcRoundPlusIcon />}
      >
        {t('market:submit.add-version')}
      </UIButton>
      <UIDialog
        open={openDialog}
        onClose={onClose}
        className="w-[calc(100vw-4rem)] mlg:w-[1200px]"
      >
        <ModelVersionUpsertDialogContent
          modelProps={props.modelProps}
          onClose={onClose}
          mutate={props.onSubmit}
        />
      </UIDialog>
    </>
  )
}

function ModelVersions(props: {
  modelId: string
  modelType?: GenerationModelType
  pagingRef?: RefObject<BaseCursorPagingRef | null>
  onClose?: () => void
}) {
  const { t } = useTranslation(['market'])
  const colorConfig = useGenerationModelColorConfig()
  const { modelId } = props

  const [{ before, after }, setCursor] = useState<{
    before?: string
    after?: string
  }>({})

  const { data, loading, refetch } = useListGenerationModelVersions({
    modelId: modelId,
    ...getBaseCursorPagingArgs({ before, after, pageSize: 5 }),
  })

  const versions = getNodesFromConnection(data).reverse()

  // pass pagingRef for updating table imperatively
  const pagingRefVal = useMemo(() => {
    return {
      mutate: refetch,
      data,
    }
  }, [refetch, data])

  useImperativeHandle(
    props.pagingRef,
    () => {
      return pagingRefVal
    },
    [pagingRefVal],
  )

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              {t('market:submit.model-version.version-name')}
            </TableCell>
            <TableCell align="right">{t('market:submit.model-type')}</TableCell>
            <TableCell align="right">
              {t('market:submit.model-version.status')}
            </TableCell>
            <TableCell align="right">
              {t('market:submit.model-version.update-time')}
            </TableCell>
            <TableCell align="right">
              {t('market:submit.model-version.actions')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {versions.length < 1 && loading && <TableRowSkeleton />}
          {versions.map(version => (
            <TableRow
              key={version.name}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell component="th" scope="row">
                <pre>{version.name ?? 'Unknown'}</pre>
              </TableCell>
              <TableCell align="right">
                <VersionTypeChip version={version} colorConfig={colorConfig} />
              </TableCell>
              <TableCell align="right">
                <ModelVersionStatusChip
                  modelVersion={version}
                  chipConfigOpts={{ showAvailable: true }}
                  type="compact"
                  className="text-black inline-flex py-1 px-2 rounded-md"
                />
              </TableCell>
              <TableCell align="right">
                {new Date(version.createdAt).toLocaleString()}
              </TableCell>
              <TableCell align="right">
                <ModelVersionEditAction
                  status={version.status}
                  versionId={version.id}
                  modelProps={{ id: props.modelId, type: props.modelType }}
                  updateTable={refetch}
                  versionExtra={version.extra}
                  onClose={props.onClose}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell
              colSpan={100}
              align="right"
              sx={{
                borderBottom: 0,
              }}
            >
              <TablePaginationActionsWrapper
                pageInfo={data?.pageInfo}
                onCursorChange={setCursor}
              />
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  )
}

function VersionTypeChip(props: {
  version: GenerationModelVersion
  colorConfig?: ModelTypeTagColors
}) {
  const { version } = props

  const versionType = getModelTypeFromVersion(version)
  const baseModelChipConfig = getModelLoraBaseModelChipConfig(
    versionType,
    props.colorConfig,
  )

  return (
    <span className="inline-flex gap-1 items-center">
      <span
        className={cx(
          baseModelChipConfig?.bgClassName,
          'text-black px-2 py-1 rounded-md text-xs',
        )}
        style={baseModelChipConfig?.bgStyle}
      >
        {baseModelChipConfig?.label}
      </span>
    </span>
  )
}

function TableRowSkeleton() {
  return (
    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
      <TableCell component="th" scope="row">
        <Skeleton />
      </TableCell>
      <TableCell align="right">
        <Skeleton />
      </TableCell>
      <TableCell align="right">
        <Skeleton />
      </TableCell>
      <TableCell align="right">
        <Skeleton />
      </TableCell>
    </TableRow>
  )
}

function ModelVersionEditAction(props: {
  status?: string
  versionId: string
  modelProps?: ModelPropsForVersionEdit
  updateTable?: () => any
  versionExtra?: GenerationModelVersionExtra
  onClose?: () => void
}) {
  const { t } = useTranslation(['market'])
  const [openEditDialog, setOpenEditDialog] = useState(false)
  const navigate = useNavigate()

  const onClose = () => {
    setOpenEditDialog(false)
  }

  // TODO: replace with useAsyncFn
  const onArchive = async () => {
    track('archive_model_version_click')

    try {
      const dialogConfirm = await UIDialog.confirm({
        heading: t('market:submit.archive-version.dialog.title'),
        content: t('market:submit.archive-version.dialog.description'),
      })

      if (!dialogConfirm) return

      track('archive_model_version_confirm', {
        modelId: props.modelProps?.id,
        versionId: props.versionId,
      })

      await executeUpsertGenerationModelVersion({
        id: props.versionId,
        input: {
          status: 'archived',
        },
      })
      props.updateTable?.()
      toast.success(t('market:submit.notice.version-archived'))
    } catch (err: any) {
      toast.error(
        t('market:submit.notice.version-archive-failed') + ': ' + err.message,
      )
    }
  }

  const onReuseDataset = () => {
    track('reuse_dataset_click', {
      location: 'model-asset-form',
    })
    navigate(
      withQuery('/train-lora', {
        addToModelId: props.modelProps?.id,
        trainingTaskId: props.versionExtra?.taskId,
      }),
    )
    props.onClose?.()
  }

  const menuItems = UIMenu.compact<UIMenuItemProps>([
    {
      id: 'edit',
      icon: <FluentDocumentEdit20Regular className="size-5" />,
      children: <Text i18nKey="market:submit.edit-version.label" />,
      onAction: () => {
        track('edit_version_click', {
          location: 'edit-model',
        })
        setOpenEditDialog(true)
      },
    },
    {
      id: 'archive',
      icon: <FluentDocumentProhibited20Regular className="size-5" />,
      children: <Text i18nKey="market:submit.archive-version.label" />,
      color: 'warning',
      onAction: () => {
        onArchive()
      },
    },
  ])

  return (
    <div className="flex gap-2 justify-end">
      {props.status === 'archived' ? (
        <UIButton variant="outlined" disabled>
          <span className="italic">
            {t('market:submit.version-status.archived')}
          </span>
        </UIButton>
      ) : (
        <>
          {isUserMultiLora(props.modelProps?.type) &&
            props.versionExtra?.taskId && (
              <UIButton
                icon={
                  <FluentImageArrowCounterclockwise20Regular className="size-5" />
                }
                onPress={() => onReuseDataset()}
              >
                <span className="whitespace-nowrap">
                  <Text i18nKey="market:submit.reuse-dataset.label" />
                </span>
              </UIButton>
            )}
          <UIMenuTrigger>
            <UIButton variant="outlined">
              <FluentMoreVertical20Filled className="size-5" />
            </UIButton>
            <UIMenu items={menuItems} placement="bottom right">
              {props => <UIMenuItem {...props} />}
            </UIMenu>
          </UIMenuTrigger>
          <UIDialog
            open={openEditDialog}
            onClose={onClose}
            className="w-[calc(100vw-4rem)] mlg:w-[1200px]"
          >
            <ModelVersionUpsertDialogWrapper
              versionId={props.versionId}
              onClose={onClose}
              modelProps={props.modelProps}
              updateTable={props.updateTable}
            />
          </UIDialog>
        </>
      )}
    </div>
  )
}

/**
 * Fetch states and form states needs to be separated
 * @param props
 * @returns
 */
function ModelVersionUpsertDialogWrapper(props: {
  versionId?: string
  onClose: () => void
  modelProps?: ModelPropsForVersionEdit
  updateTable?: () => any
}) {
  const { data, refetch } = useGetGenerationModelByVersionId(
    props.versionId || skipToken,
  )

  /**
   * Avoid refetching when version id doesn't exist, e.g. in model dialog (which doesn't have version from props)
   * Should be revisited after updating to v4.0.11
   * @see https://github.com/apollographql/apollo-client/issues/12996
   */
  const mutate = useCallback(() => {
    if (!props.versionId) return
    refetch()
  }, [props.versionId])

  return (
    <Suspense fallback={<Preloader />}>
      {props.versionId && !data ? (
        <Preloader />
      ) : (
        <ModelVersionUpsertDialogContent
          onClose={props.onClose}
          modelProps={props.modelProps}
          versionData={data}
          mutate={() => {
            props.updateTable?.()
            mutate()
          }}
        />
      )}
    </Suspense>
  )
}

/**
 * Use separated submission function
 * @param props versionId: if empty, treat as creating new version
 * @returns
 */
export function ModelVersionUpsertDialogContent(props: {
  onClose: () => void
  modelProps?: ModelPropsForVersionEdit
  mutate?: () => any
  versionData?: GenerationModelVersion
}) {
  const { versionData, mutate } = props
  const { t } = useTranslation(['market'])
  const [coverUploading, setCoverUploading] = useState(false)
  const [fileUploading, setFileUploading] = useState(false)
  const [fileId, setFileId] = useState<string>()

  const [coverMediaId, setCoverMediaId] = useState(() => versionData?.mediaId)
  const { data: coverMedia } = useGetMedia(coverMediaId ?? skipToken)

  const { values, handleChange, getValues, setValues } = useForm<VersionValues>(
    versionData
      ? {
          name: versionData.name,
          ...versionData.extra,
        }
      : undefined,
  )

  const dataSrc = versionData?.model ?? props.modelProps ?? {}
  const { id: modelId, type: modelType, isPrivate } = dataSrc

  const [{ loading }, submitVersion] = useAsyncFn(async () => {
    const val = await getValues()

    try {
      const input = getCreateVersionInputs({
        versionVals: val,
        modelId,
        fileId,
        mediaId: coverMediaId,
      })
      const res = await executeUpsertGenerationModelVersion({
        id: versionData?.id,
        input,
      })
      const newModelVersion = res.data?.upsertGenerationModelVersion

      // update existing version data
      mutate?.()
      track(
        'submit_model_version_success',
        getSubmitModelVersionTrackAttrs({
          values: val,
          modelType: newModelVersion?.model?.type,
          modelId,
          versionId: versionData?.id,
        }),
      )
      toast.success(
        props.versionData
          ? t('market:submit.notice.version-edited')
          : t('market:submit.notice.version-added'),
      )
      props.onClose()
    } catch (err: any) {
      toast.error(err.message)
    }
  }, [props.versionData?.id, fileId, coverMediaId, modelId])

  const modelArtworkPickerOptions: UseModelArtworkDataSourceOptions =
    useMemo(() => {
      const modelOptions = isLora(versionData?.modelType)
        ? {
            loraId: versionData?.modelId,
            loraVersionId: versionData?.id,
          }
        : {
            modelId: versionData?.modelId,
            modelVersionId: versionData?.id,
          }

      return {
        ...modelOptions,
        first: 30,
        isSafeSearch: true,
        orderBy: '-markInfo.likedCount',
      } as UseModelArtworkDataSourceOptions
    }, [versionData?.modelType, versionData?.modelId, versionData?.id])

  return (
    <>
      <UIDialogHeader
        heading={<Text i18nKey="market:model.action.edit-version" />}
      />
      <UIDialogContent className="@container/form p-4">
        <div className="grid grid-cols-1 @[600px]/form:grid-cols-[2fr_1fr] grid-flow-dense gap-x-8 gap-4 text-sm">
          <div className="@[600px]/form:col-start-2 flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <span className="text-primary">
                <Text i18nKey="common:cover" />
              </span>

              <ImageSource
                className="relative"
                size="md"
                value={coverMedia}
                onChange={m => {
                  if (!m) return
                  setCoverMediaId(m.id)
                }}
                onUpload={setCoverUploading}
                placeholderElement={<UploadImagePlacerholder />}
                supportedMimeTypes={['image/jpeg', 'image/png', 'image/webp']}
                dropzoneOptions={{
                  noKeyboard: true,
                }}
                features={{
                  dragDrop: true,
                  uploadButton: true,
                  taskPicker: false,
                  customPicker: true,
                  nativeGallery: false,
                  nativeCamera: false,
                  buttons: true,
                }}
                buttonProps={{
                  customPicker: {
                    variant: 'filled',
                  },
                }}
                customPickerLabel={t(
                  'action:image-source.media-picker.model-artwork.button',
                  {
                    context: isLora(versionData?.modelType)
                      ? 'lora'
                      : undefined,
                  },
                )}
                customPickerElement={(open, onPick, onCancel) => (
                  <ModelArtworkPicker
                    title={t(
                      'action:image-source.media-picker.model-artwork.title',
                    )}
                    open={open}
                    onSubmit={onPick}
                    onClose={onCancel}
                    dataSourceOptions={modelArtworkPickerOptions}
                  />
                )}
              />
            </div>
            {versionData && (
              <div className="flex flex-col gap-2">
                <span className="text-primary">
                  <Text
                    i18nKey="market:model.preview-artwork.label"
                    values={{ count: 2 }}
                  />
                </span>
                <PreviewArtworkList
                  modelVersion={versionData}
                  value={values.previewArtworkIds}
                  onChange={artworkIds =>
                    handleChange('previewArtworkIds')(artworkIds)
                  }
                  className="flex-none w-[300px] md:w-[400px] max-w-full"
                />
              </div>
            )}
          </div>
          <div className="col-start-1 flex flex-col gap-4">
            <ModelVersionForm
              versionValues={values}
              handleChange={handleChange}
              modelType={modelType}
              isEditing
            />
            {!versionData?.id && (
              <ModelFileUploadBtn
                fileId={fileId}
                setFileId={id => setFileId(id)}
                skipHash={isPrivate}
                setFileUploading={setFileUploading}
              />
            )}
          </div>
        </div>
      </UIDialogContent>
      <UIDialogFooter
        className={cx(
          'sticky bottom-0',
          // HACK: translate-y-4 for compensating padding when sticky active)
          'translate-y-4',
        )}
      >
        <UIButton variant="outlined" onPress={props.onClose}>
          {t('action:cancel')}
        </UIButton>
        <UIButton
          busy={loading}
          disabled={coverUploading || fileUploading}
          variant="filled"
          onPress={async () => {
            try {
              await submitVersion()
            } catch (err: any) {
              toast.error(err.message)
            }
          }}
          color="normal"
        >
          {t('market:submit.upload-submit')}
        </UIButton>
      </UIDialogFooter>
    </>
  )
}

export function UploadImagePlacerholder() {
  const { t } = useTranslation(['action'])

  return (
    <div className="flex flex-col items-center gap-2">
      <IcOutlineAddPhotoAlternateIcon className="text-4xl" />
      <div
        className="text-center font-medium"
        dangerouslySetInnerHTML={{
          __html: t('action:drag-and-drop-hint', { br: '<br />' }) ?? '',
        }}
      ></div>
    </div>
  )
}

function PreviewArtworkList(
  props: PropsWithChildren &
    StyleProps & {
      modelVersion: GenerationModelVersion
      value: string[] | undefined
      onChange?: (artworkIds: string[]) => void
    },
) {
  const artworkPickerRef = useRef<ArtworkPickerRef>(null)

  return (
    <>
      <div
        className={cx(
          'grid grid-cols-3 content-start gap-1 aspect-square',
          props.className,
        )}
        style={props.style}
        onClick={() => artworkPickerRef.current?.open()}
      >
        {props.value?.map(artworkId => (
          <PreviewArtworkItem key={artworkId} artworkId={artworkId} />
        ))}
        {(props.value?.length ?? 0) < 9 && (
          <button className="grid place-content-center w-full aspect-square text-neutral-500 border border-dashed border-zinc-300 dark:border-zinc-600 rounded-md">
            <FluentAdd24Filled className="size-6" />
          </button>
        )}
      </div>
      <ArtworkPicker
        ref={artworkPickerRef}
        modelVersionId={props.modelVersion.id}
        modelType={props.modelVersion.modelType}
        value={props.value}
        multiple
        max={9}
        onSubmit={artworkIds => {
          props.onChange?.(artworkIds as string[])
        }}
      />
    </>
  )
}

function PreviewArtworkItem(props: { artworkId: string }) {
  const { data: artwork } = useGetArtwork(props.artworkId)

  return (
    <MediaPreview
      mediaId={artwork?.mediaId}
      thumbnail
      className="w-full aspect-square rounded-md"
    />
  )
}
